import { useState, useRef, useCallback, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import clsx from 'clsx';

interface JamesInputProps {
  onSend: (message: string) => void;
  onStop?: () => void;
  isStreaming?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Pre-fills the input once on mount (e.g. a draft typed into the Home "Ask James" box). */
  initialDraft?: string;
}

export function JamesInput({
  onSend,
  onStop,
  isStreaming = false,
  disabled = false,
  placeholder = 'Ask James...',
  initialDraft,
}: JamesInputProps) {
  const [value, setValue] = useState(initialDraft ?? '');
  const [focused, setFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const focusedOnMount = useRef(false);
  useEffect(() => {
    if (initialDraft && !focusedOnMount.current) {
      focusedOnMount.current = true;
      textareaRef.current?.focus();
    }
  }, [initialDraft]);

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim();
    if (trimmed && !disabled) {
      onSend(trimmed);
      setValue('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  }, [value, disabled, onSend]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, [value]);

  return (
    <div className="flex-shrink-0 bg-gradient-to-b from-transparent to-surface-secondary px-4 pb-4 pt-6 sm:px-6">
      <div className="mx-auto max-w-4xl">
        <div
          className={clsx(
            'flex items-end gap-2 rounded-3xl border bg-surface-primary px-3 py-2 transition-all duration-200',
            focused
              ? 'border-text-primary shadow-medium ring-4 ring-text-primary/5'
              : 'border-border-light shadow-soft hover:border-border'
          )}
        >
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            className={clsx(
              'min-h-[28px] flex-1 resize-none bg-transparent px-2 py-1.5 text-body-sm text-text-primary placeholder-text-muted',
              'focus:outline-none',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
          />

          {isStreaming ? (
            <button
              onClick={onStop}
              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-2xl bg-error px-3 text-body-sm font-medium text-white transition-colors hover:bg-error-dark"
              title="Stop generating"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
              </span>
              Stop
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={disabled || !value.trim()}
              className={clsx(
                'group inline-flex h-9 items-center justify-center gap-1.5 rounded-2xl px-3.5 text-body-sm font-medium transition-all',
                'bg-surface-inverse text-text-inverse',
                'hover:bg-text-secondary',
                'disabled:cursor-not-allowed disabled:opacity-30'
              )}
            >
              Send
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-disabled:translate-x-0" />
            </button>
          )}
        </div>

        <div className="mt-2 hidden flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3 sm:flex">
          <p className="whitespace-nowrap text-body-xs text-text-muted">
            <kbd className="mr-1 inline-flex h-4 min-w-[18px] items-center justify-center rounded border border-border-light bg-surface-primary px-1 font-mono text-[10px] text-text-secondary">
              ↵
            </kbd>
            to send
            <span className="mx-1.5 text-text-muted">·</span>
            <kbd className="mx-1 inline-flex h-4 items-center justify-center rounded border border-border-light bg-surface-primary px-1 font-mono text-[10px] text-text-secondary">
              ⇧↵
            </kbd>
            for new line
          </p>
          <p className="whitespace-nowrap text-body-xs text-text-muted">James can be wrong, so check before acting</p>
        </div>
      </div>
    </div>
  );
}
